import { useEffect, useState } from 'react';
import CalendarHeader from './CalendarHeader';
import CalendarDay from './CalendarDay';
import CalendarGrid from './CalendarGrid';
import CalendarMonthYearSelector from './CalendarMonthYearSelector';
import { useGetCalendarDays } from '../../hooks/useGetCalendarDays';
import { formatDate, formattedDate } from '../../utils/calendar.utils';

type CalendarProps = {
    setupDate: Date;
    isRange: boolean;
    startDate: Date | null;
    endDate: Date | null;
    onDateChange: (date: Date) => void;
};
export default function Calendar({
    setupDate,
    onDateChange,
    isRange,
    startDate,
    endDate,
}: CalendarProps) {
    // This is the current date range to display for a calendar
    const [date, setDate] = useState(setupDate);
    const [selectedDate, setSelectedDate] = useState<string | null>(
        startDate ? formatDate(startDate) : null,
    );
    const [showMonthYearPicker, setShowMonthYearPicker] = useState(false);

    const handleOnDateChange = (update: Date): void => {
        setDate(update);
    };

    const handleMonthYearClick = () => {
        setShowMonthYearPicker(!showMonthYearPicker);
    };

    const handleMonthYearSelection = (year: number, month: number) => {
        setShowMonthYearPicker(false);
        setDate(new Date(`${year}-${month}-${date.getDate()}`));
    };

    const handleDaySelected = (day: string) => {
        if (!isRange) {
            setSelectedDate(day);
        }
        onDateChange(new Date(day.replaceAll('-', ',')));
    };

    // This builds the array of days to display in the grid and is updated when selected date changes.
    // This is also what marks a day as in the current month or if it is part of a selected range of dates.
    const calendarDays = useGetCalendarDays(
        date,
        selectedDate,
        isRange,
        startDate,
        endDate,
    );

    // TODO: This use effect is new and might possibly have side effects on the over all date picker or parent components
    useEffect(() => {
        if (startDate) {
            setSelectedDate(formattedDate(startDate));
        }
        // setDate(setupDate);
    }, [startDate, endDate, setupDate]);

    const Calendar = (
        <>
            <CalendarHeader
                onDateChange={handleOnDateChange}
                onMonthYearClick={handleMonthYearClick}
                date={date}
            />
            <CalendarGrid>
                {calendarDays.map((day, dayIndex) => (
                    <CalendarDay
                        key={dayIndex}
                        date={day.date}
                        isCurrentMonth={day.isCurrentMonth}
                        isSelected={day.isSelected}
                        isRangeDate={day.isRangeDate}
                        onDaySelected={handleDaySelected}
                    />
                ))}
            </CalendarGrid>
        </>
    );

    const MonthYerSelector = (
        <CalendarMonthYearSelector
            currentDate={date}
            onSelection={handleMonthYearSelection}
        />
    );

    const View = showMonthYearPicker ? MonthYerSelector : Calendar;
    return (
        <>
            <div className="alli-w-64 alli-rounded-xl alli-bg-white alli-p-3">
                {View}
            </div>
        </>
    );
}
